import React, { useEffect, useState } from 'react';
import { FiCheckCircle, FiCreditCard, FiMapPin, FiPlus, FiTrash2, FiShoppingCart, FiInfo, FiX, FiAlertTriangle, FiCheck, FiSearch, FiAlertCircle } from 'react-icons/fi';
import api from '../utils/api';
import Select from 'react-select';

// Modal visual moderno con animación de check para éxito
const SimpleModal = ({ isOpen, onClose, title, text, icon, html, confirmButtonText }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl shadow-2xl flex flex-col items-center justify-center p-10 border border-gray-100 animate-in zoom-in-95 duration-300 relative min-w-[320px] max-w-xs">
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <FiX className="w-5 h-5" />
                </button>
                <div className="flex flex-col items-center text-center">
                    {icon === 'success' && (
                        <svg className="mb-4 h-16 w-16 text-lime-400 animate-[pop_0.4s_ease]" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="24" cy="24" r="24" fill="#ECFDF5"/>
                            <path className="animate-check-stroke" d="M16 25L22 31L33 19" stroke="#84cc16" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    )}
                    {icon === 'error' && (
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                            <FiX className="w-8 h-8 text-red-600" />
                        </div>
                    )}
                    {icon === 'warning' && (
                        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                            <FiAlertTriangle className="w-8 h-8 text-yellow-600" />
                        </div>
                    )}
                    {icon === 'info' && (
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                            <FiInfo className="w-8 h-8 text-blue-600" />
                        </div>
                    )}

                    <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                    {text && <p className="text-gray-600 mb-6">{text}</p>}
                    {html && <div className="text-gray-600 mb-6" dangerouslySetInnerHTML={{ __html: html }} />}
                    {confirmButtonText && (
                        <button
                            onClick={onClose}
                            className="w-full bg-black text-white font-bold py-3 px-4 rounded-lg hover:bg-gray-800 transition-colors"
                        >
                            {confirmButtonText}
                        </button>
                    )}
                </div>
                {/* Animación CSS para el check */}
                <style>{`
                @keyframes pop {
                  0% { transform: scale(0.7); opacity: 0; }
                  80% { transform: scale(1.1); opacity: 1; }
                  100% { transform: scale(1); opacity: 1; }
                }
                .animate-[pop_0.4s_ease] {
                  animation: pop 0.4s cubic-bezier(0.4,0,0.2,1);
                }
                .animate-check-stroke {
                  stroke-dasharray: 40;
                  stroke-dashoffset: 40;
                  animation: check-stroke 0.5s 0.1s ease forwards;
                }
                @keyframes check-stroke {
                  to { stroke-dashoffset: 0; }
                }
                `}</style>
            </div>
        </div>
    );
};



const SolicitudesPage = () => {
    const [loading, setLoading] = useState(false);


    // Consulta la configuración de validación de matrículas al cargar el componente
    useEffect(() => {
        async function fetchMatriculaValidacionConfig() {
            try {
                await api.get('obtener-configuracion-validacion-matriculas-existencia');
            } catch (error) {
                console.error('Error al obtener configuración de validación de matrículas:', error);
            }
        }
        fetchMatriculaValidacionConfig();
    }, []);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const [cart, setCart] = useState([]);
    const [currentOffice, setCurrentOffice] = useState('');
    const [currentMatricula, setCurrentMatricula] = useState('');
    const [currentPlan, setCurrentPlan] = useState('express');

    const [overlayVisible, setOverlayVisible] = useState(true);
    const [matriculaError, setMatriculaError] = useState('');
    const [inmuebleInfo, setInmuebleInfo] = useState(null);
    const [loadingInmueble, setLoadingInmueble] = useState(false);
    // debounce timer ref para la consulta automática
    const debounceTimer = React.useRef(null);

    // Estado para el modal personalizado
    const [modalState, setModalState] = useState({
        isOpen: false,
        title: '',
        text: '',
        icon: 'success', // success, error, warning, info
        html: null,
        confirmButtonText: 'OK'
    });

    const showAlert = ({ title, text, icon, html, confirmButtonText = 'OK', timer }) => {
        setModalState({ isOpen: true, title, text, icon, html, confirmButtonText });
        if (timer) {
            setTimeout(() => {
                setModalState(prev => ({ ...prev, isOpen: false }));
            }, timer);
        }
    };

    const closeAlert = () => {
        setModalState(prev => ({ ...prev, isOpen: false }));
    };

    const [offices, setOffices] = useState([]);
    useEffect(() => {
        async function fetchOffices() {
            try {
                const response = await api.get('/oficinas-registro-supernotariado');
                // Ajusta el mapeo según la estructura del backend
                const data = response.data?.data || response.data;
                if (Array.isArray(data)) {
                    setOffices(data.map(o => ({ value: o.codigo || o.value, label: o.ciudad || o.label })));
                }
            } catch (error) {
                console.error('Error al obtener oficinas:', error);
            }
        }
        fetchOffices();
    }, []);

    const plans = [
        {
            id: 'basico',
            name: 'Básico',
            price: 35900,
            popular: false,
            features: ['Certificado de tradición y libertad']
        },
        {
            id: 'express',
            name: 'Express',
            price: 50900,
            popular: true,
            features: [
                'Certificado de tradición y libertad',
                'Estudio de títulos express',
                'Avalúo comercial digital',
                'Alertas contra lavado de activos',
                'Antecedentes recientes (10 años)'
            ]
        },
        {
            id: 'profesional',
            name: 'Profesional',
            price: 90900,
            popular: false,
            features: [
                'Certificado de tradición y libertad',
                'Estudio de títulos express',
                'Avalúo comercial digital',
                'Alertas contra lavado de activos',
                'Antecedentes completos'
            ]
        }
    ];

    const getPlanById = (id) => plans.find(p => p.id === id);

    const consultarInmueble = async (circulo_registro, matricula) => {
        setLoadingInmueble(true);
        setInmuebleInfo(null);
        let info = null;

        try {
            const response = await api.post('obtener-informacion-inmueble', {
                matricula,
                circulo_registro,
                telefono_codigo: '57',
                telefono: '0000000000'
            });
            // Adaptar a la estructura real del backend
            const resultado = response.data?.data?.resultado;
            const noDisponible = resultado?.datos?.estado === "No Disponible";
            if (resultado && resultado.existe && !noDisponible) {
                info = {
                    direccion: resultado?.datos?.direccion ? resultado?.datos?.direccion : 'Dirección no disponible',
                    circulo: resultado?.datos?.circulo ? resultado?.datos?.circulo : 'Circulo no disponible',
                };
                setInmuebleInfo(info);
            } else {
                info = {
                    error: (
                        <>
                            <b>No se encontró la dirección.</b>
                            <div>
                                Si necesitas ayuda escríbenos al{' '}
                                <a
                                    href={`https://api.whatsapp.com/send?phone=573122991096&text=Necesito ayuda con mi matricula: ${circulo_registro} - ${matricula}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#25D366', textDecoration: 'underline' }}
                                >
                                    WhatsApp
                                </a>
                            </div>
                        </>
                    )
                };
                setInmuebleInfo(info);
            }
        } catch {
            info = {
                error: (
                    <>
                        <b>Error al consultar la dirección.</b>
                        <div>
                            Si necesitas ayuda escríbenos al{' '}
                            <a
                                href={`https://api.whatsapp.com/send?phone=573122991096&text=Necesito ayuda con mi matricula: ${circulo_registro} - ${matricula}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#25D366', textDecoration: 'underline' }}
                            >
                                WhatsApp
                            </a>
                        </div>
                    </>
                )
            };
            setInmuebleInfo(info);
        } finally {
            setLoadingInmueble(false);
            return info;
        }
    };

    
    // Validate matricula number (only the number, office is in dropdown)
    const validateMatricula = (value) => {
        if (!value) {
            setMatriculaError('');
            return false;
        }

        // Only numbers, between 3 and 20 digits
        const matriculaRegex = /^[0-9]{3,20}$/;

        if (!matriculaRegex.test(value)) {
            setMatriculaError('Solo números (mínimo 3 dígitos)');
            return false;
        }

        setMatriculaError('');
        return true;
    };

    // effect with debounce para consultar inmueble automáticamente cuando
    // cambian la oficina o la matrícula
    useEffect(() => {
        // limpia cualquier timer previo
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        if (currentOffice && validateMatricula(currentMatricula)) {
            debounceTimer.current = setTimeout(() => {
                consultarInmueble(currentOffice, currentMatricula);
            }, 2000); // 2 segundos de debounce
        }

        // cleanup cuando el componente se desmonte o cambien dependencias
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [currentOffice, currentMatricula]);

const handleMatriculaChange = (e) => {
    const value = e.target.value;
    // Allow only numbers in input
    if (value && !/^\d*$/.test(value)) return;
    setCurrentMatricula(value);
    setInmuebleInfo(null);
    if (!value) {
        setMatriculaError('');
    }
};

    const addToCart = async (e) => {
        e.preventDefault();

        if (!currentOffice || !currentMatricula) {
            showAlert({
                title: 'Campos incompletos',
                text: 'Por favor completa todos los campos',
                icon: 'warning',
                confirmButtonColor: 'Entendido'
            });
            return;
        }

        // Validate matricula number
        if (!validateMatricula(currentMatricula)) {
            showAlert({
                title: 'Formato inválido',
                text: 'La matrícula debe contener solo números (mínimo 3 dígitos)',
                icon: 'error',
                confirmButtonColor: 'Corregir'
            });
            return;
        }

        // Ensure we have up‑to‑date inmueble info; if not, fetch it now
        let info = inmuebleInfo;
        if (!info || info.error || !info.direccion) {
            info = await consultarInmueble(currentOffice, currentMatricula);
        }

        if (!info || info.error || !info.direccion) {
            showAlert({
                title: 'Matrícula no válida',
                text: 'Debes consultar y verificar que la matrícula exista antes de agregarla al carrito.',
                icon: 'error',
                confirmButtonText: 'Entendido'
            });
            return;
        }

        const officeLabel = offices.find(o => o.value === currentOffice)?.label || currentOffice;
        const planData = getPlanById(currentPlan);
        const fullMatricula = `${currentOffice}-${currentMatricula}`;

        // Check for duplicates
        if (cart.some(item => item.fullMatricula === fullMatricula)) {
             showAlert({
                title: 'Ya existe',
                text: 'Esta matrícula ya está en tu carrito',
                icon: 'info',
                confirmButtonColor: 'Entendido'
            });
            return;
        }

        // Agregar al carrito
        setCart([
            ...cart,
            {
                id: Date.now(),
                office: currentOffice,
                officeLabel,
                matricula: currentMatricula,
                fullMatricula,
                planId: currentPlan,
                planName: planData.name,
                price: planData.price
            }
        ]);

        setCurrentOffice('');
        setCurrentMatricula('');
        setMatriculaError('');

        showAlert({
            title: '¡Agregado!',
            text: 'Matrícula agregada al carrito',
            icon: 'success',
            timer: 1500,
            confirmButtonText: null 
        });
    };

    const removeFromCart = (id) => {
        setCart(cart.filter(item => item.id !== id));
    };

    const updateCartItemPlan = (itemId, newPlanId) => {
        const planData = getPlanById(newPlanId);
        setCart(cart.map(item =>
            item.id === itemId
                ? { ...item, planId: newPlanId, planName: planData.name, price: planData.price }
                : item
        ));
    };

    const calculateTotal = () => {
        return cart.reduce((sum, item) => sum + item.price, 0);
    };

    const handlePayment = () => {
        if (cart.length === 0) {
            showAlert({
                title: 'Carrito vacío',
                text: 'Agrega al menos una matrícula',
                icon: 'warning',
                confirmButtonColor: 'Entendido'
            });
            return;
        }

        if (!acceptedTerms) {
            showAlert({
                title: 'Términos y condiciones',
                text: 'Debes aceptar los términos y condiciones para continuar',
                icon: 'warning',
                confirmButtonColor: 'Aceptar'
            });
            return;
        }

        setLoading(true);
        setTimeout(() => {
            setLoading(false);
            showAlert({
                title: '¡Redirigiendo a Mercado Pago!',
                html: `
                    <div class="text-left mt-2">
                        <p>Cantidad: <strong>${cart.length} matrícula${cart.length !== 1 ? 's' : ''}</strong></p>
                        <p class="text-xl mt-2">Total a pagar: <strong>$${calculateTotal().toLocaleString('es-CO')}</strong></p>
                    </div>
                `,
                icon: 'success',
                confirmButtonText: 'Continuar'
            });
        }, 1500);
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            <SimpleModal 
                isOpen={modalState.isOpen}
                onClose={closeAlert}
                title={modalState.title}
                text={modalState.text}
                icon={modalState.icon}
                html={modalState.html}
                confirmButtonText={modalState.confirmButtonText}
            />

            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8 text-center lg:text-left">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                        Certificados de Tradición
                    </h1>
                    <p className="text-gray-600 text-lg">
                        Selecciona el análisis que necesites y agrega las matrículas que necesitas consultar
                    </p>
                </div>

                <div className="relative">
                    {/* Overlay visible over this section (production only) */}
                    {overlayVisible && (
                        <div className="absolute inset-0 bg-black/40 bg-opacity-40 z-50 flex items-center justify-center">
                            <div className="text-center px-4">
                                <span className="text-white text-4xl font-bold uppercase tracking-wide block">
                                    Próximamente
                                </span>
                                <span className="text-white text-lg mt-2 block">
                                    Podrás comprar muchos con un solo pago
                                </span>
                            </div>
                        </div>
                    )}
                    <div className={`grid lg:grid-cols-3 gap-8 ${overlayVisible ? 'opacity-50 pointer-events-none blur-sm' : ''}`}>
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Plan Selection */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
                            <div className="flex items-center gap-2 mb-6">
                                <div className="bg-black text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">1</div>
                                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                                    Selecciona tu plan
                                </h2>
                            </div>

                            <div className="grid md:grid-cols-3 gap-4">
                                {plans.map((plan) => (
                                    <button
                                        key={plan.id}
                                        type="button"
                                        onClick={() => setCurrentPlan(plan.id)}
                                        className={`relative p-5 rounded-xl border-2 transition-all text-left flex flex-col h-full ${currentPlan === plan.id
                                            ? 'border-black shadow-lg bg-gray-50'
                                            : 'border-gray-200 hover:border-gray-300 hover:shadow-md bg-white'
                                            }`}
                                    >
                                        {plan.popular && (
                                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-bold tracking-wider uppercase rounded-full shadow-sm z-10"
                                                style={{ backgroundColor: '#eaff04', color: '#000000' }}>
                                                Más Popular
                                            </div>
                                        )}

                                        {currentPlan === plan.id && (
                                            <div className="absolute -top-2 -right-2 bg-black text-white rounded-full p-1 shadow-md z-10">
                                                <FiCheckCircle className="w-4 h-4" />
                                            </div>
                                        )}

                                        <div className="mb-4">
                                            <h3 className="text-lg font-bold text-gray-900 mb-1">{plan.name}</h3>
                                            <p className="text-2xl font-extrabold text-gray-900">
                                                ${plan.price.toLocaleString('es-CO')}
                                            </p>
                                        </div>

                                        <div className="border-t border-gray-200 my-3 w-full"></div>

                                        <ul className="space-y-2 flex-grow">
                                            {plan.features.map((feature, idx) => (
                                                <li key={idx} className="flex items-start text-xs text-gray-600 leading-tight">
                                                    <FiCheckCircle className="text-green-600 w-3 h-3 mr-2 flex-shrink-0 mt-0.5" />
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Form */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
                             <div className="flex items-center gap-2 mb-6">
                                <div className="bg-black text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">2</div>
                                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                                    Ingresa los datos
                                </h2>
                            </div>

                            <form onSubmit={addToCart} className="space-y-5">
                                <div className="grid md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            <div className="flex items-center">
                                                <FiMapPin className="mr-2 text-gray-400 w-4 h-4" />
                                                Oficina de registro
                                            </div>
                                        </label>
                                            <Select
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                value={offices.find(opt => opt.value === currentOffice)}
                                                onChange={(selected) => setCurrentOffice(selected.value)}
                                                options={offices}
                                                placeholder="Selecciona una oficina..."
                                                styles={{
                                                    control: (base, state) => ({
                                                        ...base,
                                                        borderRadius: '0.5rem',
                                                        padding: '0.15rem',
                                                        borderColor: state.isFocused ? '#000000' : '#d1d5db',
                                                        boxShadow: state.isFocused ? '0 0 0 1px #000000' : 'none',
                                                        '&:hover': {
                                                            borderColor: '#000000'
                                                        },
                                                        fontSize: '0.875rem',
                                                        fontWeight: '500'
                                                    }),
                                                    option: (base, state) => ({
                                                        ...base,
                                                        fontSize: '0.875rem',
                                                        backgroundColor: state.isSelected ? '#000000' : state.isFocused ? '#f3f4f6' : 'white',
                                                        color: state.isSelected ? 'white' : '#374151',
                                                        cursor: 'pointer',
                                                        '&:active': {
                                                            backgroundColor: '#000000'
                                                        }
                                                    }),
                                                    menu: (base) => ({
                                                        ...base,
                                                        borderRadius: '0.5rem',
                                                        overflow: 'hidden',
                                                        zIndex: 50
                                                    })
                                                }}
                                            />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                             <div className="flex items-center">
                                                <FiCreditCard className="mr-2 text-gray-400 w-4 h-4" />
                                                Matrícula inmobiliaria
                                            </div>
                                        </label>
                                        <input
                                            type="text"
                                            name="matricula"
                                            autoComplete="on"
                                            inputMode="numeric"
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-black text-gray-900 transition-colors ${matriculaError ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Ej: 1234567"
                                            value={currentMatricula}
                                            onChange={handleMatriculaChange}
                                            required
                                        />
                                        {matriculaError && (
                                            <p className="text-red-600 text-xs mt-1 flex items-center animate-pulse">
                                                <FiInfo className="mr-1 w-3 h-3"/> {matriculaError}
                                            </p>
                                        )}
                                        
                                    </div>
                                    </div>
                                    {/* Preview of full matricula */}
                                        {currentOffice && currentMatricula && !matriculaError && (
                                            <div className="mt-3 px-3 py-2 bg-white border border-gray-200 rounded-xl shadow flex items-center justify-between space-x-3 animate-in fade-in slide-in-from-top-2 duration-300 w-full">
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-sm text-gray-500 font-medium">Matrícula:</span>
                                                    <span className="font-mono text-base font-bold text-purple-600 tracking-wider">{currentOffice}</span>
                                                    <span className="text-lg font-bold text-gray-400">-</span>
                                                    <span className="font-mono text-base font-bold text-green-700 tracking-wider">{currentMatricula}</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="flex items-center gap-2 px-5 py-2 rounded-lg font-bold text-white bg-gradient-to-r from-purple-500 to-purple-400 shadow hover:from-purple-600 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-300 transition-all text-sm"
                                                    style={{ minWidth: '120px' }}
                                                    onClick={() => {
                                                        if (!currentOffice || !currentMatricula) return;
                                                        consultarInmueble(currentOffice, currentMatricula);
                                                    }}
                                                >
                                                    <FiSearch className="w-4 h-4" /> Consultar
                                                </button>
                                            </div>
                                        )}
                                        {/* only show box when there's activity (loading or after consulta) */}
                                        {currentOffice && currentMatricula && (loadingInmueble || inmuebleInfo) && (
                                            <div
                                                className={
                                                    `mt-4 p-4 rounded-xl animate-in fade-in slide-in-from-top-2 duration-300 flex items-start ` +
                                                    (loadingInmueble
                                                        ? 'bg-green-100 border-2 border-green-500'
                                                        : inmuebleInfo?.error
                                                            ? 'bg-red-100 border-2 border-red-500'
                                                            : 'bg-green-100 border-2 border-green-500')
                                                }
                                            >
                                                {loadingInmueble ? (
                                                    <div className="flex items-center space-x-3 w-full">
                                                        <svg className="animate-spin h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                                        </svg>
                                                        <span className="text-green-700 text-base font-medium">Buscando dirección del inmueble...</span>
                                                    </div>
                                                ) : inmuebleInfo ? ( inmuebleInfo.error ? (
                                                        <div className="flex items-center space-x-2 w-full">
                                                            <FiAlertCircle className="text-red-400 w-5 h-5" />
                                                            <span className="text-red-600 text-sm font-semibold">{inmuebleInfo.error}</span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="flex items-center mr-4 mt-1">
                                                                <span className="bg-green-500 rounded-full p-2 flex items-center justify-center">
                                                                    <FiMapPin className="text-white w-6 h-6" />
                                                                </span>
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="text-base font-semibold text-green-900">Dirección encontrada:</span>
                                                                <span className="text-lg font-bold text-green-900 tracking-wide mt-1">{inmuebleInfo.direccion} - {inmuebleInfo.circulo}</span>
                                                            </div>
                                                        </>
                                                    )
                                                ) : null}
                                            </div>
                                        )}
                                <button
                                    type="submit"
                                    style={{
                                        backgroundColor:
                                            !currentOffice || !currentMatricula || loadingInmueble
                                                ? '#f3f4f6'
                                                : '#eaff04',
                                        color:
                                            !currentOffice || !currentMatricula || loadingInmueble
                                                ? '#9ca3af'
                                                : '#000000',
                                        cursor:
                                            !currentOffice || !currentMatricula || loadingInmueble
                                                ? 'not-allowed'
                                                : 'pointer'
                                    }}
                                    className="w-full font-bold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center text-lg shadow-md hover:shadow-lg"
                                    disabled={
                                        !currentOffice || !currentMatricula || loadingInmueble
                                    }
                                >
                                    <FiPlus className="mr-2 w-5 h-5" />
                                    Agregar al Carrito
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* Cart Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-4">
                            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col h-[calc(100vh-2rem)] lg:h-auto lg:max-h-[85vh]">
                                {/* Cart Header */}
                                <div className="bg-gray-900 text-white p-6">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-xl font-bold flex items-center">
                                            <FiShoppingCart className="mr-2 w-5 h-5" />
                                            Tu Carrito
                                        </h2>
                                        <span className="font-bold px-3 py-1 rounded-full text-xs"
                                            style={{ backgroundColor: '#eaff04', color: '#000000' }}>
                                            {cart.length} ITEMS
                                        </span>
                                    </div>
                                </div>

                                {/* Cart Items */}
                                <div className="flex-1 overflow-y-auto min-h-[200px] lg:min-h-[300px] bg-gray-50">
                                    {cart.length === 0 ? (
                                        <div className="h-full flex flex-col items-center justify-center p-12 text-center text-gray-400">
                                            <div className="bg-gray-100 p-6 rounded-full mb-4">
                                                <FiShoppingCart className="w-8 h-8 opacity-40" />
                                            </div>
                                            <p className="font-medium text-gray-500">Tu carrito está vacío</p>
                                            <p className="text-xs mt-1 text-gray-400">Agrega matrículas para comenzar</p>
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-100">
                                            {cart.map((item) => (
                                                <div key={item.id} className="p-4 bg-white hover:bg-gray-50 transition group">
                                                    <div className="flex justify-between items-start mb-3">
                                                        <div className="flex-1">
                                                            <div className="flex items-center">
                                                                <span className="bg-gray-100 text-gray-600 text-[10px] font-mono px-1.5 py-0.5 rounded mr-2 border border-gray-200">
                                                                    {item.office}
                                                                </span>
                                                                <p className="font-bold text-gray-900 text-lg tracking-tight">
                                                                    {item.matricula}
                                                                </p>
                                                            </div>
                                                            <p className="text-[10px] text-gray-500 mt-1 uppercase tracking-wide truncate max-w-[180px]">
                                                                {item.officeLabel}
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={() => removeFromCart(item.id)}
                                                            className="text-gray-300 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-colors"
                                                            title="Eliminar"
                                                        >
                                                            <FiTrash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>

                                                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                                                        <div className="flex justify-between items-center mb-2">
                                                            <label className="text-xs font-semibold text-gray-500 uppercase">Plan</label>
                                                            <span className="text-xs font-bold text-gray-900">{item.planName}</span>
                                                        </div>
                                                        <Select
                                                            value={plans.map(p => ({ value: p.id, label: `Cambiar a ${p.name} ($${p.price.toLocaleString('es-CO')})` })).find(opt => opt.value === item.planId)}
                                                            onChange={(selected) => updateCartItemPlan(item.id, selected.value)}
                                                            options={plans.map(p => ({
                                                                value: p.id,
                                                                label: `Cambiar a ${p.name} ($${p.price.toLocaleString('es-CO')})`
                                                            }))}
                                                            styles={{
                                                                control: (base, state) => ({
                                                                    ...base,
                                                                    borderRadius: '0.375rem',
                                                                    padding: '0',
                                                                    borderColor: state.isFocused ? '#000000' : '#e5e7eb',
                                                                    boxShadow: state.isFocused ? '0 0 0 1px #000000' : 'none',
                                                                    '&:hover': {
                                                                        borderColor: '#000000'
                                                                    },
                                                                    fontSize: '0.75rem',
                                                                    backgroundColor: 'white'
                                                                }),
                                                                option: (base, state) => ({
                                                                    ...base,
                                                                    fontSize: '0.75rem',
                                                                    backgroundColor: state.isSelected ? '#000000' : state.isFocused ? '#f3f4f6' : 'white',
                                                                    color: state.isSelected ? 'white' : '#374151',
                                                                    cursor: 'pointer'
                                                                }),
                                                                menu: (base) => ({
                                                                    ...base,
                                                                    borderRadius: '0.375rem',
                                                                    overflow: 'hidden',
                                                                    zIndex: 50
                                                                })
                                                            }}
                                                        />
                                                        <div className="mt-2 text-right">
                                                            <span className="text-lg font-bold text-gray-900">
                                                                ${item.price.toLocaleString('es-CO')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Cart Footer */}
                                {cart.length > 0 && (
                                    <div className="border-t border-gray-200 p-6 bg-white shadow-[0_-5px_15px_rgba(0,0,0,0.02)] z-10">
                                        <div className="flex justify-between items-center mb-6">
                                            <span className="text-gray-600 font-medium">Total a pagar</span>
                                            <span className="text-3xl font-extrabold text-gray-900">
                                                ${calculateTotal().toLocaleString('es-CO')}
                                            </span>
                                        </div>

                                        <div className="pb-5">
                                            <label className="flex items-start cursor-pointer group select-none">
                                                <div className="relative flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={acceptedTerms}
                                                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                                                        className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-gray-300 transition-all checked:border-black checked:bg-black hover:border-gray-400 focus:ring-2 focus:ring-black focus:ring-offset-1"
                                                    />
                                                    <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 transition-opacity peer-checked:opacity-100">
                                                        <FiCheck className="h-3.5 w-3.5" />
                                                    </div>
                                                </div>
                                                <div className="ml-3 text-xs text-gray-600 leading-relaxed">
                                                    Acepto los{' '}
                                                    <a href="#" className="text-gray-900 hover:text-black font-bold underline decoration-dotted underline-offset-2">
                                                        Términos y Condiciones
                                                    </a>
                                                    {' '}y la{' '}
                                                    <a href="#" className="text-gray-900 hover:text-black font-bold underline decoration-dotted underline-offset-2">
                                                        Política de Privacidad
                                                    </a>
                                                </div>
                                            </label>
                                        </div>

                                        <button
                                            onClick={handlePayment}
                                            disabled={loading || !acceptedTerms}
                                            style={{
                                                backgroundColor: acceptedTerms && !loading ? '#eaff04' : '#f3f4f6',
                                                color: acceptedTerms && !loading ? '#000000' : '#9ca3af',
                                                cursor: acceptedTerms && !loading ? 'pointer' : 'not-allowed'
                                            }}
                                            className="w-full font-bold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center text-base shadow-sm hover:shadow-md"
                                        >
                                            {loading ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Procesando...
                                                </>
                                            ) : (
                                                <>
                                                    <FiCreditCard className="mr-2 w-5 h-5" />
                                                    Pagar con Mercado Pago
                                                </>
                                            )}
                                        </button>

                                        <div className="flex items-center justify-center gap-2 mt-4 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
                                            {/* Mock payment icons */}
                                            <div className="h-6 w-10 bg-blue-500 rounded px-1 flex items-center justify-center text-[8px] font-bold text-white italic">Visa</div>
                                            <div className="h-6 w-10 bg-red-500 rounded px-1 flex items-center justify-center text-[8px] font-bold text-white">Master</div>
                                            <div className="h-6 w-10 bg-blue-400 rounded px-1 flex items-center justify-center text-[8px] font-bold text-white">PSE</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
};

export default SolicitudesPage;
